import React, { useMemo } from 'react';
import { buildUiHelpers } from './sharedUi';

export default function IbAnalysisSection({
  COLORS,
  th,
  td,
  inputStyle,
  formatMoney,
  kontoauszugErr,
  kontoauszugMsg,
  kontoauszugLoading,
  kontoauszugFile,
  setKontoauszugFile,
  kontoauszugYear,
  setKontoauszugYear,
  ibTaxYears,
  analysiereKontoauszug,
  kontoauszugProgress,
  kontoauszugProgressText,
  kontoauszugMeta,
  kontoauszugResults,
  kontoauszugReferences,
  kontoauszugShowReferences,
  setKontoauszugShowReferences,
  kontoauszugIssues,
}) {
  const { Card, Btn } = useMemo(() => buildUiHelpers({ COLORS, th: {} }), [COLORS]);

  return (
    <div>
      <Card style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px' }}>Interactive Brokers Kontoauszug analysieren</div>
        <div style={{ color: COLORS.muted, fontSize: '14px', lineHeight: 1.8, marginBottom: '16px' }}>
          Lade einen IB-Kontoauszug als CSV hoch und wähle das Steuerjahr. Die Datei wird nur für die Auswertung verarbeitet und in dieser ersten Version nicht dauerhaft gespeichert.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 180px auto', gap: '14px', alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Dies kann mehrere Minuten dauern!</label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={e => setKontoauszugFile(e.target.files?.[0] || null)}
              style={{ ...inputStyle, marginBottom: 0, padding: '7px 10px' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Steuerjahr</label>
            <select
              value={kontoauszugYear}
              onChange={e => setKontoauszugYear(Number(e.target.value))}
              style={{ ...inputStyle, marginBottom: 0, appearance: 'none' }}
            >
              {(ibTaxYears.length > 0 ? ibTaxYears : [new Date().getFullYear()]).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <Btn onClick={analysiereKontoauszug} color="green" style={{ alignSelf: 'end' }}>
            {kontoauszugLoading ? 'Analysiere...' : 'Kontoauszug analysieren'}
          </Btn>
        </div>
        {kontoauszugLoading && (
          <div style={{ marginTop: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '6px', fontSize: '12px', color: COLORS.muted }}>
              <span>{kontoauszugProgressText || 'Analyse läuft'}</span>
              <span>{kontoauszugProgress}%</span>
            </div>
            <div style={{ height: '10px', borderRadius: '999px', background: '#ffffff10', overflow: 'hidden', border: `1px solid ${COLORS.border}` }}>
              <div
                style={{
                  width: `${Math.max(4, Math.min(100, kontoauszugProgress || 0))}%`,
                  height: '100%',
                  background: `linear-gradient(90deg, ${COLORS.accent} 0%, ${COLORS.green} 100%)`,
                  transition: 'width 300ms ease',
                }}
              />
            </div>
          </div>
        )}
        {kontoauszugErr && <div style={{ color: COLORS.red, fontSize: '13px', marginTop: '12px' }}>⚠ {kontoauszugErr}</div>}
        {kontoauszugMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginTop: '12px' }}>✓ {kontoauszugMsg}</div>}
      </Card>

      {kontoauszugMeta && (
        <Card style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '14px' }}>Metadaten</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
            <div><div style={{ color: COLORS.muted, fontSize: '12px' }}>Datei</div><div style={{ fontSize: '14px' }}>{kontoauszugMeta.filename || '—'}</div></div>
            <div><div style={{ color: COLORS.muted, fontSize: '12px' }}>Steuerjahr</div><div style={{ fontSize: '14px' }}>{kontoauszugMeta.tax_year || '—'}</div></div>
            <div><div style={{ color: COLORS.muted, fontSize: '12px' }}>Konto</div><div style={{ fontSize: '14px' }}>{kontoauszugMeta.account || '—'}</div></div>
            <div><div style={{ color: COLORS.muted, fontSize: '12px' }}>Periode</div><div style={{ fontSize: '14px' }}>{kontoauszugMeta.period || '—'}</div></div>
            <div><div style={{ color: COLORS.muted, fontSize: '12px' }}>Basiswährung</div><div style={{ fontSize: '14px' }}>{kontoauszugMeta.base_currency || '—'}</div></div>
            <div><div style={{ color: COLORS.muted, fontSize: '12px' }}>FX-Quelle</div><div style={{ fontSize: '14px' }}>{kontoauszugMeta.fx_source || '—'}</div></div>
          </div>
        </Card>
      )}

      {kontoauszugResults.length > 0 && (
        <Card style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '14px' }}>Ergebnistabelle</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>ID</th>
                  <th style={th}>Position</th>
                  <th style={{ ...th, textAlign: 'right' }}>EUR</th>
                  <th style={th}>Referenzen</th>
                  <th style={th}>Hinweis</th>
                </tr>
              </thead>
              <tbody>
                {kontoauszugResults.map(entry => (
                  <tr key={entry.id}>
                    <td style={{ ...td, fontWeight: '700' }}>{entry.id}</td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>{entry.label}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{formatMoney(entry.amount_eur)}</td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>{Array.isArray(entry.references) ? entry.references.join(', ') : '—'}</td>
                    <td style={{ ...td, whiteSpace: 'normal', color: COLORS.muted }}>{entry.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {kontoauszugReferences.length > 0 && (
        <Card style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: kontoauszugShowReferences ? '14px' : 0 }}>
            <div style={{ fontSize: '16px', fontWeight: '700' }}>Referenzwerte</div>
            <button
              onClick={() => setKontoauszugShowReferences(open => !open)}
              style={{
                background: '#ffffff08',
                color: COLORS.text,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '10px',
                padding: '8px 12px',
                cursor: 'pointer',
                fontWeight: '600',
              }}
            >
              {kontoauszugShowReferences ? 'Ausblenden' : 'Einblenden'}
            </button>
          </div>
          {kontoauszugShowReferences && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>ID</th>
                    <th style={th}>Bereich</th>
                    <th style={th}>Position</th>
                    <th style={{ ...th, textAlign: 'right' }}>EUR</th>
                    <th style={th}>Hinweis</th>
                  </tr>
                </thead>
                <tbody>
                  {kontoauszugReferences.map(entry => (
                    <tr key={entry.id}>
                      <td style={{ ...td, fontWeight: '700' }}>{entry.id}</td>
                      <td style={td}>{entry.area}</td>
                      <td style={{ ...td, whiteSpace: 'normal' }}>{entry.label}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{formatMoney(entry.amount_eur)}</td>
                      <td style={{ ...td, whiteSpace: 'normal', color: COLORS.muted }}>{entry.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {kontoauszugIssues.length > 0 && (
        <Card>
          <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>Hinweise / Pruefungen</div>
          <ul style={{ margin: 0, paddingLeft: '18px', color: COLORS.muted, lineHeight: 1.8 }}>
            {kontoauszugIssues.map((issue, index) => (
              <li key={`${issue}-${index}`}>{issue}</li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
